/**
 * Values shared by client-side and server-side validation so the two can never
 * drift apart. The server re-validates everything regardless of the client.
 */

/* -------------------------------------------------------------------------- */
/* Size limits                                                                 */
/* -------------------------------------------------------------------------- */

export const LIMITS = {
  usernameMin: 3,
  usernameMax: 30,
  passwordMin: 10,
  passwordMax: 200,
  emailMax: 254,
  /**
   * The youngest an account holder may be.
   *
   * Fifteen rather than thirteen: Thailand's PDPA treats consent given by
   * somebody under twenty as needing a guardian's involvement in some cases,
   * and fifteen is where a good deal of Thai online service draws its own
   * line. It is checked at registration — a birth date collected and never
   * looked at is personal data held for no reason.
   */
  minAgeYears: 15,
  /** Nobody alive is older; a date beyond this is a typo, not a birthday. */
  maxAgeYears: 120,
  displayNameMin: 1,
  displayNameMax: 60,
  bioMax: 500,
  messageMax: 4000,
  interestsMax: 15,
  interestMax: 40,
  /**
   * Jobs, and places studied — each capped on its own.
   *
   * Ten is more than anybody will fill in and few enough that a profile stays
   * a profile rather than a CV. It is also the ceiling the `position` column
   * is sized for: a `TINYINT UNSIGNED` holds 255, so the limit is the product
   * decision rather than the column's.
   */
  affiliationsMax: 10,
  organisationMax: 120,
  affiliationRoleMax: 120,
  /** Nothing before this is a job or a degree; it is a typo. */
  affiliationMinYear: 1900,
  /** Avatar / cover upload ceiling, in bytes. */
  uploadMaxBytes: 5 * 1024 * 1024,
  /**
   * How long one voice message may run.
   *
   * A product decision, not a technical ceiling: a minute is the length at
   * which a recording is still a message rather than a monologue. The listener
   * cannot skim it, cannot search it and — in this product above all — may not
   * share the language it is in, so length costs them more here than elsewhere.
   */
  voiceMaxSeconds: 60,
  /**
   * And how many bytes that may take.
   *
   * A minute of WebM/Opus from Chrome is well under 500 KB and a minute of
   * Safari's MP4/AAC is around 1 MB, so 2 MB holds the longest allowed
   * recording from any browser with room to spare — and refuses a file that is
   * something other than what it says it is long before ffmpeg is asked to
   * parse it.
   */
  voiceMaxBytes: 2 * 1024 * 1024,
  pageSizeDefault: 20,
  pageSizeMax: 50,
} as const;

/* -------------------------------------------------------------------------- */
/* Username rules                                                              */
/* -------------------------------------------------------------------------- */

/** a-z, 0-9 and underscore. Must start with a letter or digit. */
export const USERNAME_PATTERN = /^[a-z0-9][a-z0-9_]{2,29}$/;

/**
 * Names that would collide with routes, impersonate the platform, or be
 * mistaken for an official account.
 */
/**
 * Slugs a page may not take.
 *
 * These are the first path segment of a URL, so anything that collides with a
 * real route would make that route unreachable - and a page called `settings`
 * or `admin` is worth having for its own sake to whoever gets there first.
 */
export const RESERVED_PAGE_SLUGS: ReadonlySet<string> = new Set([
  'about', 'admin', 'api', 'app', 'assets', 'auth', 'blog', 'chat', 'contact',
  'dashboard', 'discover', 'docs', 'explore', 'feed', 'help', 'home', 'legal',
  'login', 'logout', 'me', 'messages', 'moderation', 'new', 'news', 'notifications',
  'p', 'page', 'pages', 'post', 'posts', 'privacy', 'profile', 'register',
  'robots', 'root', 'search', 'security', 'settings', 'signin', 'signup',
  'sitemap', 'static', 'status', 'support', 'system', 'talkvibe', 'terms',
  'user', 'users', 'www',
]);

/** Lower-case letters, digits and single hyphens, not at either end. */
export const PAGE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const RESERVED_USERNAMES: ReadonlySet<string> = new Set([
  'admin', 'administrator', 'root', 'superuser', 'sysadmin', 'moderator', 'mod',
  'support', 'help', 'helpdesk', 'contact', 'billing', 'sales', 'security',
  'abuse', 'legal', 'privacy', 'terms', 'status', 'staff', 'team', 'official',
  'talkvibe', 'talk_vibe', 'talkvibeapp', 'connect', 'system', 'bot', 'null',
  'undefined', 'anonymous', 'guest', 'everyone', 'here', 'all', 'me', 'you',
  'api', 'www', 'app', 'auth', 'login', 'logout', 'register', 'signup',
  'signin', 'settings', 'profile', 'profiles', 'user', 'users', 'discover',
  'friends', 'messages', 'chat', 'chats', 'conversation', 'conversations',
  'notification', 'notifications', 'search', 'explore', 'home', 'about',
  'media', 'static', 'assets', 'upload', 'uploads', 'cdn', 'ws', 'realtime',
]);

/* -------------------------------------------------------------------------- */
/* Countries - ISO 3166-1 alpha-2                                              */
/* -------------------------------------------------------------------------- */

/**
 * Stored as the code only. Human-readable names are resolved at render time
 * with Intl.DisplayNames, which keeps names localised and avoids shipping (and
 * having to maintain) a translated country table.
 */
const COUNTRY_CODE_LIST =
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ ' +
  'BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR ' +
  'CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR ' +
  'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ' +
  'ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ ' +
  'LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ ' +
  'MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF ' +
  'PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI ' +
  'SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR ' +
  'TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW';

export const COUNTRY_CODES: readonly string[] = Object.freeze(
  COUNTRY_CODE_LIST.split(' '),
);

const COUNTRY_CODE_SET: ReadonlySet<string> = new Set(COUNTRY_CODES);

export function isValidCountry(code: string): boolean {
  return COUNTRY_CODE_SET.has(code);
}

/* -------------------------------------------------------------------------- */
/* Languages                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Languages a user may pick as their chat language. Deliberately limited to
 * tags the translation providers support, so we never promise a translation we
 * cannot deliver.
 */
export const LANGUAGE_CODES: readonly string[] = Object.freeze([
  'ar', 'bg', 'bn', 'cs', 'da', 'de', 'el', 'en', 'es', 'fa', 'fi', 'fr', 'he',
  'hi', 'hu', 'id', 'it', 'ja', 'km', 'ko', 'lo', 'ms', 'my', 'ne', 'nl', 'no',
  'pl', 'pt', 'ro', 'ru', 'si', 'sk', 'sv', 'sw', 'ta', 'te', 'th', 'tl', 'tr',
  'uk', 'ur', 'vi', 'zh', 'zh-TW',
]);

/**
 * How long an emailed link stays usable.
 *
 * Here rather than beside the SQL that issues them, because the pages that
 * request one tell the person how long they have. Those sentences were
 * written by hand and one of them already said the wrong number — a promise
 * of fifteen minutes against a token that lives thirty. Both sides read this.
 *
 * The reset window is deliberately the shorter of the two: that token grants
 * a password change, and a verification link only confirms an address.
 */
export const TOKEN_TTL_MINUTES = {
  passwordReset: 30,
  emailVerification: 60 * 24,
} as const;

const LANGUAGE_CODE_SET: ReadonlySet<string> = new Set(LANGUAGE_CODES);

export function isValidLanguage(code: string): boolean {
  return LANGUAGE_CODE_SET.has(code);
}

/* -------------------------------------------------------------------------- */
/* Interests                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Interests are stored as slugs from this catalogue. Keeping them closed makes
 * the /discover interest filter exact-matchable and index-friendly, and stops
 * the tag space fragmenting on day one.
 */
export const INTERESTS: readonly string[] = Object.freeze([
  'technology', 'programming', 'ai', 'design', 'startup', 'business',
  'finance', 'science', 'space', 'health', 'fitness', 'food', 'cooking',
  'travel', 'photography', 'music', 'movies', 'anime', 'books', 'writing',
  'art', 'fashion', 'gaming', 'esports', 'sports', 'football', 'basketball',
  'running', 'cycling', 'nature', 'animals', 'gardening', 'languages',
  'education', 'history', 'philosophy', 'politics', 'volunteering',
  'parenting', 'mindfulness',
]);

const INTEREST_SET: ReadonlySet<string> = new Set(INTERESTS);

export function isValidInterest(slug: string): boolean {
  return INTEREST_SET.has(slug);
}

/* -------------------------------------------------------------------------- */
/* Uploads                                                                     */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* Profile extras                                                              */
/* -------------------------------------------------------------------------- */

export const PROFILE_VISIBILITIES = ['public', 'friends', 'private'] as const;
export const LANGUAGE_PROFICIENCIES = ['learning', 'conversational', 'fluent', 'native'] as const;

export const PROFILE_LIMITS = {
  maxSpokenLanguages: 8,
  maxSocialLinks: 5,
  socialLinkLabelMax: 40,
  socialLinkUrlMax: 200,
  timezoneMax: 64,
} as const;

/**
 * Schemes permitted in a social link.
 *
 * These are user-supplied URLs rendered as anchors, so anything that can
 * execute — `javascript:`, `data:`, `vbscript:` — must never get through.
 * An allow-list is the only safe shape here; a deny-list always misses one.
 */
export const ALLOWED_LINK_SCHEMES: ReadonlySet<string> = new Set(['http:', 'https:']);

export const UPLOAD_KINDS = [
  'avatar',
  'cover',
  'story',
  'post',
  'message',
  'listing',
  'voice',
] as const;
export type UploadKind = (typeof UPLOAD_KINDS)[number];

/**
 * The kinds that belong to a profile, and so are set by uploading them.
 *
 * `story` is an upload but not a profile field: the asset is created first and
 * attached to a story afterwards. Keeping the two lists apart is what stops
 * `POST /api/me/story` being generated by the profile-upload loop, where the
 * "avatar or else cover" branch would have quietly set the story as a cover.
 */
export const PROFILE_UPLOAD_KINDS = ['avatar', 'cover'] as const;
export type ProfileUploadKind = (typeof PROFILE_UPLOAD_KINDS)[number];

/** Accepted image types, mapped to the extension we assign server-side. */
export const ALLOWED_IMAGE_TYPES: Readonly<Record<string, string>> = Object.freeze({
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
});

/**
 * Accepted *recording* types, mapped to the extension we assign server-side.
 *
 * A separate map, deliberately, and not more entries in the one above. Every
 * upload route in this product — avatar, cover, story, post photo, page cover,
 * listing photo, message photo — goes through the single method that validates
 * against `ALLOWED_IMAGE_TYPES`, which is keyed by `string`, so TypeScript would
 * not have said a word: adding audio to it would have made a sound file a valid
 * profile picture in one stroke.
 *
 * These are what browsers actually record. Safari produces MP4/AAC and Chrome,
 * Edge and Firefox produce WebM/Opus; neither can play what the other records,
 * which is why what is stored here is not what is served — see the audio
 * processor.
 */
export const ALLOWED_AUDIO_TYPES: Readonly<Record<string, string>> = Object.freeze({
  'audio/mp4': 'm4a',
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
});

/**
 * What every recording is converted to, whatever it arrived as.
 *
 * AAC-LC in an MP4 container is the one combination every browser in this
 * product's reach can play, Safari included — and Safari is the half of the
 * audience that cannot play WebM at all.
 */
export const VOICE_OUTPUT_TYPE = 'audio/mp4';
export const VOICE_OUTPUT_EXTENSION = 'm4a';
